import { useMemo, useCallback } from 'react'

import { getCountryByCode } from 'shared/utils/country'
import { ArrayElement } from 'shared/types'
import {
  labelCodeIdVar,
  labelCodeVar,
  labelTitleVar,
  spaceIdVar
} from 'modulesCommon/store'
import { resetAllFilters } from 'shared/utils/resetAllFilters'
import { useCountry } from 'modulesCommon/serviceHooks'

import { useGetZonesQuery, GetZonesQuery } from '../graphql/queries/getZones.types'
import { useCurrentUser } from '../../modules/User/useCurrentUser'


export type TZone = ArrayElement<NonNullable<GetZonesQuery['getLabels']>>

type TOptionAdapter<T> = (data: TZone) => T

const zoneOptionAdapter = (label: TZone) => ({ value: label?.id ?? '', label: label?.name ?? '' })

export const ZONE_ID_PERSIST_KEY = 'labelCode'

/**
 * @deprecated use useZones instead
 * @param optionAdapter
 */
export const useZonesList = <Option = {
  value: string,
  label: string
}>(optionAdapter: TOptionAdapter<Option> = zoneOptionAdapter as TOptionAdapter<Option>) => {
  const { data: zoneListResponse, loading: zoneLoading, error: zoneError } = useGetZonesQuery()

  const { getCountry, setCountry } = useCountry()
  const { user } = useCurrentUser()

  const zoneList = zoneListResponse?.getLabels ?? []

  const byId = useMemo<Record<string, TZone>>(
    () => (zoneListResponse?.getLabels ?? []).reduce<Record<string, TZone>>((acc, val) => ({
      ...acc,
      [val.id || '']: val
    }), {}),
    [zoneListResponse?.getLabels]
  )

  const zoneOptions = useMemo<Option[]>(
    () => (zoneListResponse?.getLabels ?? []).map(optionAdapter),
    [optionAdapter, zoneListResponse?.getLabels]
  )

  const getZoneById = useCallback<(id: string | undefined) => TZone | undefined>(
    (id) => id ? byId[id] : undefined,
  [byId]
  )

  const getUnitsByZoneId = useCallback((id: string | undefined) => getZoneById(id)?.units ?? [], [getZoneById])

  const getCountryOptions = useCallback(
    (id?: string) => {
      if (typeof id !== 'string') return []
      return (byId[id]?.country_codes ?? [])
        .reduce<Array<{ label: string, value: string }>>((a, value) => {
          if (typeof value !== 'string') return a
          try {
            const label = getCountryByCode(value)
            if (typeof label !== 'string') return a
            return [...a, { label, value }]
          } catch {
            // TODO: Log this case
            return a
          }
        }, [])
    },
    [byId]
  )

  // TODO: Should be reconsidering
  /**
   * @deprecated
   */
  const setZone = useCallback<(label_id: string, countryCode?: string) => void>(
    (label_id, country_code) => {
      const zone = getZoneById(label_id)
      resetAllFilters()
      labelCodeIdVar(label_id)
      localStorage.setItem(ZONE_ID_PERSIST_KEY, label_id)
      if (typeof country_code !== 'undefined') {
        setCountry(country_code)
      } else {
        const currentCountry = getCountry()
        if (currentCountry && zone?.country_codes?.includes(currentCountry)) {
          // if country is from current zone, do not change it
          setCountry(currentCountry)
        } else if (zone?.id === user?.label_id) {
          // if user's zone is selected, set country to user's country

          if (user?.country_code) setCountry(user.country_code)
        } else {
          // if not user's zone, set country to first country in zone
          const countries = getCountryOptions(label_id)
          countries[0]?.value && setCountry(countries[0].value)
        }
      }
      if (typeof zone !== 'undefined') {
        labelTitleVar(zone.name)
        labelCodeVar(zone.alias)
        spaceIdVar('')
      }
      // user is not in the dependency list because it is not used in the function
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getCountryOptions, getZoneById, setCountry]
  )

  return {
    getUnitsByZoneId,
    getZoneById,
    getCountryOptions,
    setZone,
    zoneList,
    zoneOptions,
    zoneLoading,
    zoneError
  }
}
